/**
 * RelatedPosts - site-wide Related Articles editorial module
 *
 * Design goals:
 * - Full-bleed dark contrast band (slate-900) so the section is visually distinct from the article body
 * - Featured (first) card: large hero image left, text right - magazine asymmetric layout
 * - Up to 3 compact secondary cards in a responsive grid
 * - Amber accent bar animates in on hover for each card
 * - "Why related" pill shows the most specific shared tag
 * - "New" badge for posts published within 30 days
 * - Read-time badge on every card
 * - Broken-image fallback: branded gradient placeholder
 * - SSR-safe visibility: related cards render immediately before JavaScript executes
 * - GA4 custom event on every click
 * - JSON-LD ItemList injected for entity graph / internal link authority
 * - Fully accessible: aria-labels, semantic article elements, keyboard-navigable links
 */

import { useState } from "react";
import { Link } from "wouter";
import { CalendarDays, Clock, ArrowRight, BookOpen, Flame, Sparkles } from "lucide-react";
import { trackRelatedPostClick } from "@/lib/analytics";
import { ResponsiveImage } from "@/components/ResponsiveImage";

import SchemaScript from "@/components/SchemaScript";
// ── Types ─────────────────────────────────────────────────────────────────────
interface RelatedPost {
  slug: string;
  title: string;
  excerpt: string;
  heroImage: string;
  date: string;
  tags: string[];
  content: string;
  author?: string;
}

interface RelatedPostsProps {
  posts: RelatedPost[];
  currentTags: string[];
  currentSlug: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────
function readingTime(content: string) {
  return Math.max(1, Math.ceil(content.split(/\s+/).length / 200));
}

/**
 * Pick the most specific shared tag: prefer the rarest tag in currentTags
 * (shortest label = most specific in this taxonomy), then fall back to post's first tag.
 */
function bestSharedTag(postTags: string[], currentTags: string[]): string | null {
  const shared = postTags.filter((t) => currentTags.includes(t));
  if (shared.length === 0) return postTags[0] ?? null;
  // Prefer shorter tag names as a proxy for specificity
  return shared.sort((a, b) => a.length - b.length)[0];
}

/**
 * Produce a bounded relevance line from the published article excerpt and the
 * reader's current topic. This deliberately makes no claim beyond the article
 * data already rendered in the card, so it is safe to prerender and cite.
 */
function contextualSummary(post: RelatedPost, currentTags: string[]): string {
  const tag = bestSharedTag(post.tags, currentTags);
  const normalizedExcerpt = post.excerpt.replace(/\s+/g, " ").trim();
  const firstSentence = normalizedExcerpt.match(/^.*?[.!?](?=\s|$)/)?.[0] ?? normalizedExcerpt;
  const excerpt = firstSentence.length > 150 ? `${firstSentence.slice(0, 147).trimEnd()}…` : firstSentence;
  return tag ? `Connects the ${tag} thread: ${excerpt}` : excerpt;
}

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function isNew(dateStr: string): boolean {
  const published = new Date(dateStr).getTime();
  const now = Date.now();
  return now - published < 30 * 24 * 60 * 60 * 1000; // 30 days
}

function trackRelatedClick(
  fromSlug: string,
  toSlug: string,
  position: number,
  placement: "bottom" | "mid-article"
) {
  trackRelatedPostClick({ from_slug: fromSlug, to_slug: toSlug, position, placement });
}

// ── Fallback image placeholder ─────────────────────────────────────────────────
function FallbackImage({ title }: { title: string }) {
  return (
    <div
      aria-hidden="true"
      className="w-full h-full flex flex-col items-center justify-center
        bg-gradient-to-br from-slate-800 via-slate-700 to-amber-900/30 select-none"
    >
      <Sparkles className="w-8 h-8 text-amber-400/60 mb-2" />
      <span className="text-[10px] font-semibold uppercase tracking-widest text-amber-400/50 text-center px-3 line-clamp-2">
        {title}
      </span>
    </div>
  );
}

// ── Image with fallback ────────────────────────────────────────────────────────
function PostImage({
  src,
  alt,
  className,
  width,
  height,
}: {
  src: string;
  alt: string;
  className?: string;
  width: number;
  height: number;
}) {
  const [errored, setErrored] = useState(false);

  if (errored) {
    return <FallbackImage title={alt} />;
  }

  return (
    <ResponsiveImage
      src={src}
      alt={alt}
      width={width}
      height={height}
      sizes={width <= 160 ? "80px" : "(min-width: 768px) 40vw, calc(100vw - 32px)"}
      onError={() => setErrored(true)}
      className={className}
    />
  );
}

// ── Featured card (first post) ─────────────────────────────────────────────────
function FeaturedCard({
  post,
  currentTags,
  currentSlug,
}: {
  post: RelatedPost;
  currentTags: string[];
  currentSlug: string;
}) {
  const tag = bestSharedTag(post.tags, currentTags);
  const mins = readingTime(post.content);
  const fresh = isNew(post.date);
  const relevance = contextualSummary(post, currentTags);

  return (
    <div>
      <Link href={`/blog/${post.slug}/`}>
        <article
          onClick={() => trackRelatedClick(currentSlug, post.slug, 1, "bottom")}
          aria-label={`Read: ${post.title}`}
          className="group relative flex flex-col md:flex-row rounded-2xl overflow-hidden
            bg-white/5 border border-white/10
            hover:border-amber-400/40 hover:bg-white/[0.08]
            transition-all duration-300 ease-out cursor-pointer"
        >
          {/* Amber accent bar - slides in from left on hover */}
          <div
            className="absolute inset-y-0 left-0 w-1 bg-amber-400 scale-y-0 origin-bottom
              group-hover:scale-y-100 transition-transform duration-300 ease-out rounded-l-2xl"
          />

          {/* Hero image - left 45% on desktop */}
          <div className="relative overflow-hidden flex-shrink-0 md:w-[45%] aspect-[16/9] md:aspect-auto">
            <PostImage
              src={post.heroImage}
              alt={post.title}
              width={600}
              height={400}
              className="w-full h-full object-cover transition-transform duration-500 ease-out
                group-hover:scale-105"
            />
            {/* Gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent to-black/20 md:block hidden" />
            {/* Position badge */}
            <div
              className="absolute top-3 left-3 w-7 h-7 rounded-full bg-amber-400 text-black
                text-xs font-bold flex items-center justify-center shadow-lg"
            >
              1
            </div>
            {/* "New" badge */}
            {fresh && (
              <div
                className="absolute top-3 right-3 px-2 py-0.5 rounded-full
                  bg-emerald-500 text-white text-[10px] font-bold uppercase tracking-wider shadow"
              >
                New
              </div>
            )}
          </div>

          {/* Card body */}
          <div className="flex flex-col flex-1 p-6 md:p-8 justify-center">
            {/* "Featured read" label */}
            <div className="flex items-center gap-2 mb-3">
              <Flame className="w-3.5 h-3.5 text-amber-400" />
              <span className="text-xs font-semibold uppercase tracking-widest text-amber-400">
                Featured Read
              </span>
            </div>

            {/* Shared tag pill */}
            {tag && (
              <div className="mb-3">
                <span
                  className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1
                    rounded-full bg-amber-400/15 text-amber-300 border border-amber-400/25 uppercase tracking-wide"
                >
                  {tag}
                </span>
              </div>
            )}

            {/* Title */}
            <h3
              className="font-serif text-xl md:text-2xl text-white leading-snug mb-3
                group-hover:text-amber-300 transition-colors duration-200 line-clamp-3"
            >
              {post.title}
            </h3>

            {/* Excerpt */}
            <p className="text-sm text-white/60 leading-relaxed line-clamp-2 mb-6">
              {post.excerpt}
            </p>

            <p className="text-xs text-amber-100/70 leading-relaxed mb-6 line-clamp-2">
              <span className="font-semibold text-amber-300">Why this is relevant: </span>
              {relevance}
            </p>

            {/* Meta + CTA row */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4 text-xs text-white/50">
                <span className="flex items-center gap-1.5">
                  <CalendarDays className="w-3.5 h-3.5" />
                  {formatDate(post.date)}
                </span>
                <span className="flex items-center gap-1.5">
                  <Clock className="w-3.5 h-3.5" />
                  {mins} min read
                </span>
              </div>
              <span
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-400
                  opacity-0 group-hover:opacity-100 translate-x-1 group-hover:translate-x-0
                  transition-all duration-200"
              >
                Read now
                <ArrowRight className="w-3.5 h-3.5" />
              </span>
            </div>
          </div>
        </article>
      </Link>
    </div>
  );
}

// ── Compact secondary card ─────────────────────────────────────────────────────
function CompactCard({
  post,
  currentTags,
  currentSlug,
  position,
}: {
  post: RelatedPost;
  currentTags: string[];
  currentSlug: string;
  position: number;
}) {
  const tag = bestSharedTag(post.tags, currentTags);
  const mins = readingTime(post.content);
  const fresh = isNew(post.date);
  const relevance = contextualSummary(post, currentTags);

  return (
    <div>
      <Link href={`/blog/${post.slug}/`}>
        <article
          onClick={() => trackRelatedClick(currentSlug, post.slug, position, "bottom")}
          aria-label={`Read: ${post.title}`}
          className="group relative flex gap-4 rounded-xl overflow-hidden p-4
            bg-white/5 border border-white/10
            hover:border-amber-400/40 hover:bg-white/[0.08]
            transition-all duration-300 ease-out cursor-pointer h-full"
        >
          {/* Amber accent bar */}
          <div
            className="absolute inset-y-0 left-0 w-0.5 bg-amber-400 scale-y-0 origin-bottom
              group-hover:scale-y-100 transition-transform duration-300 ease-out rounded-l-xl"
          />

          {/* Thumbnail */}
          <div className="relative flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden bg-white/10">
            <PostImage
              src={post.heroImage}
              alt={post.title}
              width={80}
              height={80}
              className="w-full h-full object-cover transition-transform duration-500 ease-out
                group-hover:scale-110"
            />
            {/* Position badge */}
            <div
              className="absolute top-1 left-1 w-5 h-5 rounded-full bg-amber-400 text-black
                text-[10px] font-bold flex items-center justify-center shadow"
            >
              {position}
            </div>
            {/* "New" badge */}
            {fresh && (
              <div
                className="absolute bottom-1 right-1 px-1.5 py-0.5 rounded-full
                  bg-emerald-500 text-white text-[8px] font-bold uppercase tracking-wider"
              >
                New
              </div>
            )}
          </div>

          {/* Text */}
          <div className="flex flex-col flex-1 min-w-0 justify-between">
            {/* Tag */}
            {tag && (
              <span className="text-[10px] font-semibold uppercase tracking-wider text-amber-400/80 mb-1 block">
                {tag}
              </span>
            )}

            {/* Title */}
            <h3
              className="text-sm font-semibold text-white leading-snug line-clamp-2
                group-hover:text-amber-300 transition-colors duration-200 mb-1"
            >
              {post.title}
            </h3>

            <p className="text-[11px] leading-snug text-white/50 line-clamp-2 mb-2">
              <span className="font-semibold text-amber-300/90">Why relevant: </span>
              {relevance}
            </p>

            {/* Meta */}
            <div className="flex items-center gap-3 text-[11px] text-white/40">
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {mins} min
              </span>
              <span className="flex items-center gap-1">
                <CalendarDays className="w-3 h-3" />
                {formatDate(post.date)}
              </span>
            </div>
          </div>

          {/* Arrow - visible on hover */}
          <ArrowRight
            className="w-4 h-4 text-amber-400 flex-shrink-0 self-center
              opacity-0 group-hover:opacity-100 -translate-x-1 group-hover:translate-x-0
              transition-all duration-200"
          />
        </article>
      </Link>
    </div>
  );
}

// ── Main export ────────────────────────────────────────────────────────────────
export default function RelatedPosts({ posts, currentTags, currentSlug }: RelatedPostsProps) {
  if (!posts || posts.length === 0) return null;

  // Show up to 4 posts: 1 featured + up to 3 compact
  const displayPosts = posts.slice(0, 4);
  const [featured, ...rest] = displayPosts;

  // JSON-LD ItemList for internal link entity graph
  const itemListSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "@id": `https://integrated.social/blog/${currentSlug}/#related-articles`,
    name: "Related Articles",
    itemListElement: displayPosts.map((p, i) => ({
      "@type": "ListItem",
      position: i + 1,
      url: `https://integrated.social/blog/${p.slug}/`,
      name: p.title,
    })),
  };

  return (
    <section
      aria-labelledby="related-posts-heading"
      data-testid="related-articles-grid"
      className="mt-0 -mx-4 sm:-mx-6 lg:-mx-8 xl:-mx-16 px-4 sm:px-6 lg:px-8 xl:px-16
        pt-10 pb-14 bg-slate-900 rounded-b-2xl"
    >
      {/* JSON-LD */}
      <SchemaScript schema={JSON.stringify(itemListSchema)} />

      {/* Section header */}
      <div className="flex items-end justify-between mb-8 gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-amber-400 mb-1.5 flex items-center gap-1.5">
            <BookOpen className="w-3.5 h-3.5" />
            Related Articles
          </p>
          <h2
            id="related-posts-heading"
            className="font-serif text-2xl md:text-3xl text-white"
          >
            Continue with Related Articles
          </h2>
          <p className="text-sm text-white/50 mt-1">
            {displayPosts.length} article{displayPosts.length !== 1 ? "s" : ""} selected for topical relevance
          </p>
        </div>
        <Link
          href="/blog/"
          className="shrink-0 inline-flex items-center gap-1.5 text-sm font-medium
            text-white/50 hover:text-amber-400 transition-colors duration-200 group"
        >
          All articles
          <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-0.5" />
        </Link>
      </div>

      {/* ── Layout: 1 post ── */}
      {displayPosts.length === 1 && (
        <FeaturedCard
          post={displayPosts[0]}
          currentTags={currentTags}
          currentSlug={currentSlug}
        />
      )}

      {/* ── Layout: 2 posts - equal grid ── */}
      {displayPosts.length === 2 && (
        <div className="grid md:grid-cols-2 gap-4">
          {displayPosts.map((p, i) => (
            <CompactCard
              key={p.slug}
              post={p}
              currentTags={currentTags}
              currentSlug={currentSlug}
              position={i + 1}
            />
          ))}
        </div>
      )}

      {/* ── Layout: 3 posts - featured left (60%), two compact right (40%) ── */}
      {displayPosts.length === 3 && (
        <div className="grid md:grid-cols-5 gap-5">
          <div className="md:col-span-3">
            <FeaturedCard
              post={featured}
              currentTags={currentTags}
              currentSlug={currentSlug}
            />
          </div>
          <div className="md:col-span-2 flex flex-col gap-4">
            {rest.map((p, i) => (
              <CompactCard
                key={p.slug}
                post={p}
                currentTags={currentTags}
                currentSlug={currentSlug}
                position={i + 2}
              />
            ))}
          </div>
        </div>
      )}

      {/* ── Layout: 4 posts - featured top-left (60%), 3 compact right column ── */}
      {displayPosts.length >= 4 && (
        <div className="grid md:grid-cols-5 gap-5">
          {/* Featured card - spans 3 of 5 columns */}
          <div className="md:col-span-3">
            <FeaturedCard
              post={featured}
              currentTags={currentTags}
              currentSlug={currentSlug}
            />
          </div>
          {/* 3 compact cards stacked - spans 2 of 5 columns */}
          <div className="md:col-span-2 flex flex-col gap-3">
            {rest.slice(0, 3).map((p, i) => (
              <CompactCard
                key={p.slug}
                post={p}
                currentTags={currentTags}
                currentSlug={currentSlug}
                position={i + 2}
              />
            ))}
          </div>
        </div>
      )}

      {/* Bottom CTA strip */}
      <div className="mt-10 pt-8 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4">
        <p className="text-sm text-white/40 text-center sm:text-left">
          Explore 100+ AI marketing insights from the Integrated.Social editorial team
        </p>
        <Link
          href="/blog/"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl
            bg-amber-400 hover:bg-amber-300
            text-black text-sm font-semibold
            transition-all duration-200 group shadow-lg shadow-amber-400/20"
        >
          <BookOpen className="w-4 h-4" />
          Browse all articles
          <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-0.5" />
        </Link>
      </div>
    </section>
  );
}
